/**
 * Inline chip that surfaces audio/microphone permission state directly in
 * the avatar panel instead of dimming the whole page with a modal.
 *
 * Visible whenever `useVoiceBrowserAccess.browserAccessPrompt` is set —
 * i.e. the SDK couldn't get audio playback or microphone access and is
 * waiting for the user to grant it. Tap the chip and we re-run the
 * permission flow (`retryBrowserAccess`), which calls `getUserMedia`
 * again from a user-gesture context (browsers require this).
 *
 * Why a chip and not the BrowserAccessPromptModal:
 *   The modal portals to `document.body`, so when the avatar lives
 *   inside a chat sidebar (Camlin help-page assistant, Architect/Cara
 *   contextual chats, DecisionMemory's AvatarChat) the modal covers the
 *   whole app surface — the user can't even see the message they were
 *   reading while granting permission. The chip floats over the avatar
 *   itself, doesn't grab focus, and disappears the moment access is
 *   granted.
 */

interface AudioPermissionChipProps {
  prompt: {
    kind: 'audio' | 'microphone';
    title: string;
    message: string;
  } | null;
  onGrant: () => void | Promise<void>;
}

export function AudioPermissionChip({ prompt, onGrant }: AudioPermissionChipProps) {
  if (!prompt) {
    return null;
  }

  const icon = prompt.kind === 'microphone' ? '🎙️' : '🔊';
  const ctaLabel = prompt.kind === 'microphone' ? 'Allow microphone' : 'Allow audio';

  return (
    <button
      type="button"
      onClick={() => { void onGrant(); }}
      title={prompt.message}
      style={{
        position: 'absolute',
        top: 12,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 30,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 14px',
        borderRadius: 999,
        border: '1px solid rgba(255, 165, 0, 0.55)',
        background: 'linear-gradient(180deg, rgba(255, 159, 64, 0.95) 0%, rgba(255, 122, 26, 0.95) 100%)',
        color: '#fff',
        fontSize: 13,
        fontWeight: 600,
        boxShadow: '0 4px 16px rgba(255, 122, 26, 0.35)',
        cursor: 'pointer',
        maxWidth: 'calc(100% - 24px)',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      <span aria-hidden="true">{icon}</span>
      <span>{ctaLabel}</span>
    </button>
  );
}

export default AudioPermissionChip;
